import React, { useState } from "react";
import {
  Box,
  IconButton,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Chip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { brand, shellGradientH, text } from "../../theme/tokens";

/**
 * Top bar for the org dashboard. Shows the organization name and a user menu
 * with a working logout (useAuth().logout). The hamburger (below lg) opens the
 * mobile drawer via the onMenuClick callback from DashLayout.
 */
const Navbar = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const { user, organization, role, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleLogout = async () => {
    setAnchorEl(null);
    await logout();
    navigate("/login", { replace: true });
  };

  const initial = (user?.name || "U").charAt(0).toUpperCase();
  const isSuper = role === "superadmin";
  const homePath = isSuper ? "/superadmin" : "/dashboard";
  const title = isSuper ? "Platform Console" : organization?.name || "Cable CRM";

  return (
    <Box
      sx={{
        height: 64,
        px: { xs: 1.5, sm: 3 },
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundImage: shellGradientH,
        color: text.onDark,
        position: "sticky",
        top: 0,
        zIndex: 10,
        boxShadow: "0 2px 12px rgba(0,0,0,0.3)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        <IconButton
          onClick={onMenuClick}
          sx={{ color: text.onDark, display: { lg: "none" } }}
          aria-label="open navigation"
        >
          <MenuIcon />
        </IconButton>
        <Typography
          sx={{ fontWeight: 600, fontSize: { xs: 16, sm: 18 }, minWidth: 0 }}
          noWrap
        >
          {title}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
        <Chip
          label={role || "user"}
          size="small"
          sx={{
            display: { xs: "none", sm: "inline-flex" },
            backgroundColor: "rgba(251,189,44,0.18)",
            color: brand.amber,
            fontWeight: 600,
            textTransform: "capitalize",
          }}
        />
        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small" aria-label="account menu">
          <Avatar sx={{ width: 34, height: 34, bgcolor: brand.amber, color: brand.onAmber, fontWeight: 700 }}>
            {initial}
          </Avatar>
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography sx={{ fontWeight: 600 }} noWrap>
              {user?.name || "User"}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {user?.email || ""}
            </Typography>
          </Box>
          <Divider />
          <MenuItem onClick={() => { setAnchorEl(null); navigate(homePath); }}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            {isSuper ? "Overview" : "Dashboard"}
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default Navbar;
